import { type LeadBoard, type LeadCard, type LeadStage, type LostReason, LEAD_SOURCES, LOST_REASONS } from '@dnx/contracts';
import { Alert, Badge, Button, cn, Dialog, Field, Input, Select } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { Megaphone, MessageCircle, Plus, Search } from 'lucide-react';
import { type DragEvent, useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { AREA_LABELS, daysAgo, initials, LOST_LABELS, SOURCE_LABELS, STAGE_DOT, STAGE_LABELS } from '../lib/labels.js';
import { meQuery } from '../lib/session.js';
import { LeadDrawer } from './lead-drawer.js';
import { NewLeadDialog } from './new-lead-dialog.js';
import { type BoardFilters, boardQuery } from './queries.js';
import { TagChips } from '../reception/tags.js';
import { tagsQuery } from '../reception/queries.js';

export interface LeadsSearch { lead?: string; meus?: boolean; origem?: string; etiqueta?: string }
const route = getRouteApi('/app/leads');

function Card({ lead, onOpen, draggable, onDragState }: {
  lead: LeadCard; onOpen: () => void; draggable: boolean; onDragState: (dragging: boolean) => void;
}) {
  return (
    <li
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/lead', JSON.stringify({ id: lead.id, version: lead.version, stage: lead.stage }));
        e.dataTransfer.effectAllowed = 'move';
        onDragState(true);
      }}
      onDragEnd={() => onDragState(false)}
      className={cn('group rounded-lg border border-border bg-surface p-3 shadow-card transition-shadow hover:border-border-strong hover:shadow-pop',
        draggable && 'cursor-grab active:cursor-grabbing')}
    >
      <button type="button" onClick={onOpen} className="block w-full text-left">
        <span className="flex items-start justify-between gap-2">
          <span className="text-[13.5px] font-medium text-fg group-hover:text-primary">{lead.contact.name}</span>
          {lead.assignedTo && (
            <span title={`Responsável: ${lead.assignedTo.name}`} className="grid size-6 shrink-0 place-items-center rounded-full bg-surface-muted text-[10px] font-semibold text-fg-muted ring-1 ring-border">
              {initials(lead.assignedTo.name)}
            </span>
          )}
        </span>
        <span className="mt-2 flex flex-wrap gap-1">
          <Badge tone={lead.source === 'meta_ads' || lead.source === 'google_ads' ? 'primary' : 'neutral'}>
            {lead.source === 'meta_ads' && <Megaphone className="size-3" aria-hidden />}{SOURCE_LABELS[lead.source]}
          </Badge>
          {lead.procedureArea && <Badge>{AREA_LABELS[lead.procedureArea]}</Badge>}
          {lead.lostReason && <Badge tone="neutral">{LOST_LABELS[lead.lostReason]}</Badge>}
        </span>
        {lead.tags.length > 0 && <TagChips tags={lead.tags} className="mt-1.5" />}
        {lead.campaign && <span className="mt-1.5 block truncate text-[11.5px] text-fg-muted">“{lead.campaign}”</span>}
      </button>
      <span className="mt-2.5 flex items-center justify-between text-[11.5px] text-fg-subtle">
        <span>{daysAgo(lead.stageChangedAt)} nesta etapa</span>
        {lead.conversationId && (
          <Link to="/conversas" search={{ filtro: 'open', c: lead.conversationId }} aria-label={`Conversa com ${lead.contact.name}`}
            className="grid size-7 place-items-center rounded-md text-fg-muted hover:bg-surface-muted hover:text-primary">
            <MessageCircle className="size-4" />
          </Link>
        )}
      </span>
    </li>
  );
}

export function LeadsPage() {
  const search = route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const canWrite = me.permissions.includes('crm.lead.write');
  const { data: tagList } = useQuery({ ...tagsQuery, enabled: me.permissions.includes('contact.tag.apply') });
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const filters: BoardFilters = { meus: search.meus, origem: search.origem, etiqueta: search.etiqueta, q: debounced || undefined };
  const { data: board } = useQuery(boardQuery(filters));
  const [over, setOver] = useState<LeadStage | null>(null);
  const [lostDrop, setLostDrop] = useState<{ id: string; version: number } | null>(null);
  const [reason, setReason] = useState<LostReason>('price');
  const [creating, setCreating] = useState(false);
  const [dragging, setDragging] = useState(false);

  const setSearch = (patch: Partial<LeadsSearch>) => void navigate({ to: '/leads', search: { ...search, ...patch }, replace: true });

  const move = useMutation({
    mutationFn: (m: { id: string; version: number; stage: LeadStage; lostReason?: LostReason }) =>
      api(`/crm/leads/${m.id}/stage`, { method: 'POST', body: { stage: m.stage, version: m.version, lostReason: m.lostReason } }),
    // otimista: o card muda de coluna na hora; se falhar (versão), o quadro recarrega
    onMutate: async (m) => {
      const key = boardQuery(filters).queryKey;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<LeadBoard>(key);
      if (prev) {
        const card = prev.columns.flatMap((c) => c.leads).find((l) => l.id === m.id);
        if (card) {
          qc.setQueryData<LeadBoard>(key, { columns: prev.columns.map((c) => ({
            ...c,
            leads: c.stage === m.stage ? [{ ...card, stage: m.stage, stageChangedAt: new Date().toISOString() }, ...c.leads.filter((l) => l.id !== m.id)] : c.leads.filter((l) => l.id !== m.id),
            total: c.stage === m.stage ? c.total + 1 : c.leads.some((l) => l.id === m.id) ? c.total - 1 : c.total,
          })) });
        }
      }
      return { prev };
    },
    onError: (_e, _m, ctx) => { if (ctx?.prev) qc.setQueryData(boardQuery(filters).queryKey, ctx.prev); },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['crm'] }),
  });

  const onDrop = (stage: LeadStage) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    setDragging(false);
    const raw = e.dataTransfer.getData('text/lead');
    if (!raw) return;
    const { id, version, stage: from } = JSON.parse(raw) as { id: string; version: number; stage: LeadStage };
    if (from === stage) return;
    if (stage === 'lost') setLostDrop({ id, version });
    else move.mutate({ id, version, stage });
  };

  const openTotal = board?.columns.filter((c) => c.stage !== 'won' && c.stage !== 'lost').reduce((a, c) => a + c.total, 0) ?? 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface px-4 py-4 sm:px-8">
        <div className="mr-auto">
          <h1 className="text-xl font-semibold tracking-tight text-fg">Leads</h1>
          <p className="text-[13px] text-fg-muted">{openTotal} em aberto no funil · fechados e perdidos dos últimos 30 dias</p>
        </div>
        <div className="relative w-56">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle" aria-hidden />
          <Input aria-label="Buscar lead" placeholder="Buscar por nome" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select aria-label="Origem" className="w-40" value={search.origem ?? ''} onChange={(e) => setSearch({ origem: e.target.value || undefined })}>
          <option value="">Todas as origens</option>
          {LEAD_SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
        </Select>
        {!!tagList?.length && (
          <Select aria-label="Etiqueta" className="w-44" value={search.etiqueta ?? ''} onChange={(e) => setSearch({ etiqueta: e.target.value || undefined })}>
            <option value="">Todas as etiquetas</option>
            {tagList.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
        )}
        <button type="button" aria-pressed={!!search.meus} onClick={() => setSearch({ meus: search.meus ? undefined : true })}
          className={cn('h-10 rounded-md border px-3 text-sm font-medium', search.meus ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong bg-surface text-fg-muted hover:text-fg')}>
          Só os meus
        </button>
        {canWrite && <Button onClick={() => setCreating(true)}><Plus className="size-4" />Novo lead</Button>}
      </header>
      {move.error && <Alert tone="warning" className="mx-4 mt-3 sm:mx-8">{move.error.message}</Alert>}

      <div className="min-h-0 flex-1 overflow-x-auto bg-bg">
        <div className="flex h-full min-w-max gap-3 px-4 py-4 sm:px-8">
          {board?.columns.map((col) => (
            <section key={col.stage} aria-label={STAGE_LABELS[col.stage]}
              onDragOver={(e) => { if (canWrite) { e.preventDefault(); setOver(col.stage); } }}
              onDragLeave={() => setOver((o) => (o === col.stage ? null : o))}
              onDrop={onDrop(col.stage)}
              className={cn('flex h-full w-[272px] flex-col rounded-xl border bg-surface-muted/60 transition-colors',
                over === col.stage ? 'border-primary bg-primary-soft/60' : 'border-transparent',
                (col.stage === 'won' || col.stage === 'lost') && 'w-[240px] opacity-90')}>
              <h2 className="flex items-center gap-2 px-3 pt-3 pb-2 text-[13px] font-semibold text-fg">
                <span className={cn('size-2 rounded-full', STAGE_DOT[col.stage])} aria-hidden />
                {STAGE_LABELS[col.stage]}
                <span className="ml-auto rounded-full bg-surface px-2 text-[11.5px] font-medium text-fg-muted tabular-nums">{col.total}</span>
              </h2>
              <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-3">
                {col.leads.map((l) => <Card key={l.id} lead={l} draggable={canWrite} onDragState={setDragging} onOpen={() => setSearch({ lead: l.id })} />)}
                {col.leads.length === 0 && (
                  <li className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-[12px] text-fg-subtle">
                    {canWrite ? 'Arraste um lead para cá' : 'Nenhum lead'}
                  </li>
                )}
              </ul>
            </section>
          ))}
        </div>
      </div>

      {/* Ao arrastar: destinos de encerramento sempre visíveis (as colunas finais podem estar fora da tela). */}
      {dragging && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-20 flex justify-center gap-4 px-4">
          {(['won', 'lost'] as const).map((stage) => (
            <div key={stage} role="region" aria-label={`Soltar em ${STAGE_LABELS[stage]}`}
              onDragOver={(e) => { e.preventDefault(); setOver(stage); }}
              onDragLeave={() => setOver((o) => (o === stage ? null : o))}
              onDrop={onDrop(stage)}
              className={cn('pointer-events-auto grid h-20 w-64 place-items-center rounded-xl border-2 border-dashed text-sm font-semibold shadow-pop backdrop-blur transition-colors',
                stage === 'won' ? 'border-success/60 bg-success-soft/95 text-success' : 'border-border-strong bg-surface/95 text-fg-muted',
                over === stage && (stage === 'won' ? 'border-success bg-success-soft' : 'border-danger bg-danger-soft text-danger'))}>
              {stage === 'won' ? 'Soltar aqui: fechado' : 'Soltar aqui: perdido'}
            </div>
          ))}
        </div>
      )}

      <LeadDrawer id={search.lead} canWrite={canWrite} onClose={() => setSearch({ lead: undefined })} />
      <NewLeadDialog open={creating} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setSearch({ lead: id }); }} />
      <Dialog open={!!lostDrop} onClose={() => setLostDrop(null)} title="Marcar como perdido" description="O motivo ajuda a entender onde o funil está vazando.">
        <form className="grid gap-4 p-5" onSubmit={(e) => { e.preventDefault(); if (lostDrop) { move.mutate({ ...lostDrop, stage: 'lost', lostReason: reason }); setLostDrop(null); } }}>
          <Field label="Motivo da perda">
            <Select autoFocus value={reason} onChange={(e) => setReason(e.target.value as LostReason)}>
              {LOST_REASONS.map((r) => <option key={r} value={r}>{LOST_LABELS[r]}</option>)}
            </Select>
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setLostDrop(null)}>Cancelar</Button>
            <Button type="submit" variant="danger">Marcar como perdido</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
